import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { Menu as MenuIcon } from '@mui/icons-material'
import LanguageSwitcher from './LanguageSwitcher'
import ThemeToggle from './ThemeToggle'
import UserMenu from './UserMenu'
import { usePortalStore } from '../stores/portalStore'

interface TopBarProps {
  onMenuToggle: () => void
}

export default function TopBar({ onMenuToggle }: TopBarProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { config } = usePortalStore()

  const topbarColor = config?.topbarColor || theme.palette.primary.main
  const topbarTextColor = config?.topbarTextColor || '#ffffff'
  const institutionName = config?.institutionName || 'Flexilabs'
  const logoUrl = config?.logoUrl || ''
  const glassmorphism = (config?.glassmorphism ?? false) && config?.backgroundMode === 'image'

  const barSx = glassmorphism
    ? {
        bgcolor: `${topbarColor}cc`,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: 1,
        borderColor: 'rgba(255,255,255,0.15)',
      }
    : {
        bgcolor: topbarColor,
        borderBottom: 1,
        borderColor: 'rgba(255,255,255,0.12)',
      }

  return (
    <AppBar
      position="fixed"
      elevation={glassmorphism ? 0 : 1}
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        color: topbarTextColor,
        ...barSx,
      }}
    >
      <Toolbar>
        {isMobile && (
          <IconButton color="inherit" edge="start" onClick={onMenuToggle} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
          {logoUrl && (
            <Box
              component="img"
              src={logoUrl}
              alt={institutionName}
              sx={{ height: 32, objectFit: 'contain', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none'
              }}
            />
          )}
          <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
            {institutionName}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LanguageSwitcher />
          <ThemeToggle />
          <UserMenu />
        </Box>
      </Toolbar>
    </AppBar>
  )
}
